import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import Loader from '../../common/components/Loader.tsx';
import { axiosInstance } from '../../common/axiosInstans.ts';
import {
    TextField,
    Select,
    MenuItem,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    InputAdornment,
    Paper,
    Card,
    CardContent,
    Typography,
    TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EmptyTablePlaceholder from '../../common/components/EmptyTablePlaceholder.tsx';

const StorageManagement = () => {
    const [storages, setstorages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('active');
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        fetchstorages();
    }, [filter]);

    const fetchstorages = async () => {
        setIsLoading(false);
        try {
            let data;
            if (filter === 'inactive') {
                data = await axiosInstance.get('/storageLocation/inactive');
            } else {
                data = await axiosInstance.get('/storagelocation');
            }
            setstorages(data.data);
        } catch (error) {
            console.error('Error fetching storages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredstorages = storages.filter((storage) => {
        return (
            storage.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            storage.location?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    });

    const paginatedData = filteredstorages.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Paper className="p-4 sm:p-6">
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Hantera lagerställen
            </Typography>

            {/* Controls Section */}
            <Card className="mb-4 p-3">
                <CardContent className="flex flex-col sm:flex-row gap-3 flex-wrap items-start sm:items-center">
                    <TextField
                        placeholder="Sök lagerställe..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        className="w-full sm:w-[250px]"
                    />

                    <Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        size="small"
                        className="w-full sm:w-auto"
                    >
                        <MenuItem value="active">Aktiva</MenuItem>
                        <MenuItem value="inactive">Inaktiva</MenuItem>
                    </Select>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(NavigationRoutes.createStorage)}
                        className="w-full sm:w-auto"
                    >
                        <AddIcon className="mr-2" /> Nytt lagerställe
                    </Button>
                </CardContent>
            </Card>

            {/* Table with horizontal scroll on mobile */}
            <div className="w-full overflow-x-auto rounded-lg border">
                <TableContainer component={Paper}>
                    <Table stickyHeader className="min-w-[600px]">
                        <TableHead>
                            <TableRow>
                                <TableCell>Namn</TableCell>
                                <TableCell>Plats</TableCell>
                                <TableCell>Beskrivning</TableCell>
                                <TableCell>Akutväska</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.length === 0 ? (
                                <EmptyTablePlaceholder
                                    colSpan={4}
                                    title="Inga lagerställen att visa"
                                    subtitle="Justera filter/sökning eller klicka på ‘Ny lagerställe’ för att skapa en ny."
                                />
                            ) : (
                                paginatedData.map((storage) => (
                                    <TableRow
                                        key={storage.id}
                                        onClick={() =>
                                            navigate(
                                                `${NavigationRoutes.storageView}/${storage.id}`,
                                                { state: { id: storage.id } }
                                            )
                                        }
                                        className="odd:bg-gray-100 hover:cursor-pointer"
                                    >
                                        <TableCell>{storage.name}</TableCell>
                                        <TableCell>
                                            {storage.location}
                                        </TableCell>
                                        <TableCell>
                                            {storage.description}
                                        </TableCell>
                                        <TableCell>
                                            {storage.isSatelliteStorage
                                                ? 'Ja'
                                                : 'Nej'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Pagination */}
            <div className="flex justify-end mt-3">
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredstorages.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Antal poster:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                    }
                />
            </div>
        </Paper>
    );
};

export default StorageManagement;
